import React, { useState, useEffect } from 'react';
import Notification from '../../components/Notification/Notification';
import imageCompression from 'browser-image-compression';
import './HeroImageUploader.css'; // We'll create this CSS file

export default function HeroImageUploader() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [images, setImages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  const imagesPerPage = 6;

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://gladious-constructions.onrender.com/api/hero-images');
      if (response.ok) {
        const data = await response.json();
        const transformed = data.map((img) => {
          const blurUrl = img.imageUrl.replace('/upload/', '/upload/w_20,q_10,e_blur:300/');
          return { ...img, blurUrl };
        });
        setImages(transformed);
      } else {
        // Fallback demo data
        setImages([
          { _id: '1', imageUrl: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80' },
          { _id: '2', imageUrl: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80' },
          { _id: '3', imageUrl: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80' },
          { _id: '4', imageUrl: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80' },
          { _id: '5', imageUrl: 'https://images.unsplash.com/photo-1583608205776-bfd35f0d9f83?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80' },
          { _id: '6', imageUrl: 'https://images.unsplash.com/photo-1613977257363-707ba9348227?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80' },
          { _id: '7', imageUrl: 'https://images.unsplash.com/photo-1571939228382-b2f2b585ce15?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80' },
          { _id: '8', imageUrl: 'https://images.unsplash.com/photo-1448630360428-65456885c650?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80' }
        ]);
      }
    } catch (error) {
      console.error('Error fetching images:', error);
    }
    setLoading(false);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const options = {
        maxSizeMB: 2,
        maxWidthOrHeight: 1680,
        useWebWorker: true,
      };
      const compressedFile = await imageCompression(file, options);
      setSelectedFile(compressedFile);
    } catch (error) {
      console.error("Compression error:", error);
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedFile);

      const response = await fetch('https://gladious-constructions.onrender.com/api/hero-images/upload', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        setSelectedFile(null);
        fetchImages();
        document.getElementById('heroUploaderFileInput').value = '';
        setNotification({ show: true, message: 'Image uploaded successfully!', type: 'success' });
        
        setTimeout(() => {
          setNotification({ show: false, message: '', type: '' });
        }, 3000);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setNotification({ show: true, message: 'Upload failed!', type: 'error' });
      
      setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 3000);
    }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    setDeletingId(id);

    try {
      const response = await fetch(`https://gladious-constructions.onrender.com/api/hero-images/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setNotification({ show: true, message: 'Image deleted successfully!', type: 'success' });
        fetchImages();

        const totalPages = Math.ceil((images.length - 1) / imagesPerPage);
        if (currentPage > totalPages && totalPages > 0) {
          setCurrentPage(totalPages);
        }
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      setNotification({ show: true, message: 'Delete failed!', type: 'error' });
    } finally {
      setDeletingId(null);

      setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 3000);
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(images.length / imagesPerPage);
  const startIndex = (currentPage - 1) * imagesPerPage;
  const endIndex = startIndex + imagesPerPage;
  const currentImages = images.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <>
      {notification.show && (
        <Notification type={notification.type} message={notification.message} />
      )}
      
      <div className="hero-uploader-container">
        {/* Upload Section */}
        <div className="hero-uploader-upload-section">
          <h2 className="hero-uploader-title">
            Upload Hero Section Image
          </h2>
          
          <div className="hero-uploader-input-group">
            <input 
              id="heroUploaderFileInput"
              type="file" 
              onChange={handleFileChange}
              accept="image/*"
              className="hero-uploader-file-input"
            />
            <button 
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className={`hero-uploader-upload-btn ${!selectedFile || uploading ? 'hero-uploader-upload-btn--disabled' : ''}`}
            >
              {uploading ? 'Uploading...' : 'Upload Image'}
            </button>
          </div>
        </div>

        {/* Images Grid */}
        <div className="hero-uploader-gallery">
          <div className="hero-uploader-header">
            <h3 className="hero-uploader-subtitle">
              Hero Images ({images.length} total)
            </h3>
            {totalPages > 1 && (
              <div className="hero-uploader-page-info">
                Page {currentPage} of {totalPages}
              </div>
            )}
          </div>

          {loading ? (
            <div className="hero-uploader-loading">
              Loading images...
            </div>
          ) : (
            <>
              <div className="hero-uploader-grid">
                {currentImages.map((img) => (
                  <div key={img._id} className="hero-uploader-card">
                    <div className="hero-uploader-image-container">
                      <img
                        src={img.imageUrl}
                        alt="Hero"
                        loading="lazy"
                        className="hero-uploader-image"
                        style={{ backgroundImage: `url(${img.blurUrl})` }}
                      />
                    </div>
                    <div className="hero-uploader-card-actions">
                      <button
                        onClick={() => handleDelete(img._id)}
                        disabled={deletingId === img._id}
                        className={`hero-uploader-delete-btn ${deletingId === img._id ? 'hero-uploader-delete-btn--disabled' : ''}`}
                      >
                        {deletingId === img._id ? 'Deleting...' : 'Delete Image'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="hero-uploader-pagination">
                  <button
                    onClick={goToPrevious}
                    disabled={currentPage === 1}
                    className={`hero-uploader-pagination-btn ${currentPage === 1 ? 'hero-uploader-pagination-btn--disabled' : ''}`}
                  >
                    Previous
                  </button>

                  {/* Page Numbers */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => goToPage(page)}
                      className={`hero-uploader-pagination-btn ${page === currentPage ? 'hero-uploader-pagination-btn--active' : ''}`}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    onClick={goToNext}
                    disabled={currentPage === totalPages}
                    className={`hero-uploader-pagination-btn ${currentPage === totalPages ? 'hero-uploader-pagination-btn--disabled' : ''}`}
                  >
                    Next
                  </button>
                </div>
              )}

              {/* Results Info */}
              {images.length > 0 && (
                <div className="hero-uploader-results-info">
                  Showing {startIndex + 1}-{Math.min(endIndex, images.length)} of {images.length} images
                </div>
              )}
            </>
          )}

          {!loading && images.length === 0 && (
            <div className="hero-uploader-empty-state">
              No images uploaded yet. Upload your first hero image!
            </div>
          )}
        </div>
      </div>
    </>
  );
}